import { AdditionalDetails } from '../../../../../types/FirestoreUser'

/**
 * Areas that do not require a side selection for symptoms
 */
const AREAS_NOT_REQUIRING_SIDE = [
  'Upper Back',
  'Lower Back',
  'Neck',
  'Speech',
  'Swallowing',
  'Throat',
  'Tongue',
]

/**
 * Validates whether all required fields in Additional Details are complete.
 * This function determines if the "P2" badge should be shown for a user.
 *
 * @param details - The additional details object to validate
 * @param extraInfoRequired - Whether extra info is required (true for PALS users)
 * @returns boolean | undefined - true if complete, false if incomplete, undefined if not applicable
 */
export function validateAdditionalDetailsComplete(
  details: Partial<AdditionalDetails>,
  extraInfoRequired: boolean
): boolean | undefined {
  // Non-PALS users (medical/administrator): don't set isComplete
  if (!extraInfoRequired) {
    return undefined
  }

  // Check ALS Onset
  if (!details.alsOnset) {
    return false
  }

  // Check Onset Classification
  if (!details.alsOnsetClassification) {
    return false
  }

  // Check Familial Gene (only if classification is 'familial')
  if (details.alsOnsetClassification === 'familial' && !details.familialGene) {
    return false
  }

  // Check all 5 contributing factors
  const factors = details.contributingFactors || {}
  if (
    !factors.primary ||
    !factors.secondary ||
    !factors.tertiary ||
    !factors.quaternary ||
    !factors.quinary
  ) {
    return false
  }

  // Check symptoms (need at least 3)
  const symptoms = details.symptoms || []
  if (symptoms.length < 3) {
    return false
  }

  // Validate first 3 symptoms
  for (let i = 0; i < 3; i++) {
    const symptom = symptoms[i]

    // Area is required
    if (!symptom?.area) {
      return false
    }

    // Side is required unless area doesn't require it
    const sideRequired = !AREAS_NOT_REQUIRING_SIDE.includes(symptom.area)
    if (sideRequired && !symptom.side) {
      return false
    }
  }

  // All validations passed
  return true
}
